import {IFrameMessages} from "../messaging/IFrameMessages";
import {merchantLocalStorage, merchantSessionStorage} from "./merchant-state/MerchantStorage";
import {generateUniqueId} from "../../blinkpay/UtilsLib";
import {EventSender, AnalyticsClient, type AnalyticsEvent} from "../utils/AnalyticsClient";
import {MERCHANT_STORAGE_KEYS} from "../Constants";
import {makeUniqueSelector} from "../utils/SelectorCalculator.js";
import {onCurrentAndFuturePageAccess} from "../utils/PageAccessHelpers";
import {allIFrameElements, onNewIFrameElementMounted, type IFrameElement} from "../ui/elements/IFrameElement";
import {loadReferrerUrl} from "../utils/Referrer";
import {userDataModule} from "./UserDataModule";
import {
    getMerchant,
    getMerchantId,
    isMerchantActive,
    onPublicDataLoaded
} from "./PublicData";
import {Dispatchable, Dispatcher} from "../../stem-core/base/Dispatcher";
import {iframeToSdkChannel} from "../messaging/IframeToSdkChannel";

class AnalyticsModule {
    allEventsDispatcher = new Dispatcher();
    eventsByTypeDispatcher = new Dispatchable();
    shouldStoreCookies: boolean | null = null; // Don't initialize the cookie storage yet.
    client = new AnalyticsClient(EventSender.sdk);
    localId?: string;
    sessionId?: string;

    init(): void {
        onCurrentAndFuturePageAccess(() => this.updateEnvironment(this.getDefaultEnvironment()));

        this.initDispatchers();

        onPublicDataLoaded(() => {
            if (!isMerchantActive()) {
                return;
            }
            const merchantId = getMerchantId();
            if (!merchantId) {
                return;
            }
            const identity: Record<string, any> = {
                merchantId,
            };
            const merchant = getMerchant();
            if (merchant && merchant.alias) {
                identity.merchantAlias = merchant.alias;
            }
            this.client.updateIdentity(identity);
            this.broadcastEnvironment();
        });

        onNewIFrameElementMounted.addListener((iframeElement: IFrameElement) => {
            iframeElement.sendMessage(IFrameMessages.UPDATE_ANALYTICS_DATA_FROM_SDK, {
                environment: this.client.environment,
                identity: this.client.identity,
                iframeSelector: makeUniqueSelector(iframeElement.node),
            });
        });

        userDataModule.authenticationChange.addListener(() => {
            if (userDataModule.userData.userId) {
                this.client.identity.userId = userDataModule.userData.userId;
            } else {
                delete this.client.identity.userId;
            }
            this.broadcastEnvironment();
        })
    }

    setEnableStorage(value: boolean): void {
        // TODO @tier2 enabling analytics should be configurable per merchant
        if (value === this.shouldStoreCookies) {
            return;
        }
        this.shouldStoreCookies = value;
        if (value) {
            // Force load the analytics identifiers from storage.
            this.localId = merchantLocalStorage.getItem(MERCHANT_STORAGE_KEYS.merchantLocalId) || this.localId;
            this.sessionId = merchantSessionStorage.getItem(MERCHANT_STORAGE_KEYS.merchantSessionId) || this.sessionId;
        } else {
            delete this.localId;
            delete this.sessionId;
        }
        this.initIdentity();
        this.broadcastEnvironment(); // TODO @mustfix this shouldn't be done manually here
    }

    initIdentity(): void {
        if (this.shouldStoreCookies) {
            this.localId = this.localId || merchantLocalStorage.getItem(MERCHANT_STORAGE_KEYS.merchantLocalId);
            if (!this.localId) {
                this.localId = generateUniqueId();
                merchantLocalStorage.setItem(MERCHANT_STORAGE_KEYS.merchantLocalId, this.localId);
            }

            this.sessionId = this.sessionId || merchantSessionStorage.getItem(MERCHANT_STORAGE_KEYS.merchantSessionId);
            if (!this.sessionId) {
                this.sessionId = generateUniqueId();
                merchantSessionStorage.setItem(MERCHANT_STORAGE_KEYS.merchantSessionId, this.sessionId);
            }
        }
        // TODO @branch don't we also put in a Blink third party identity?
        this.client.updateIdentity({
            localId: this.localId,
            sessionId: this.sessionId,
        });
    }

    broadcastEnvironment(): void {
        allIFrameElements.forEach(iFrameElement => {
            iFrameElement.sendMessage(IFrameMessages.UPDATE_ANALYTICS_DATA_FROM_SDK, {
                identity: this.client.identity,
                environment: this.client.environment,
            });
        });
    }

    getDefaultEnvironment(): Record<string, string> {
        return {
            referrerURL: loadReferrerUrl(),
            url: window.location.href,
        }
    }

    updateEnvironment(data: Record<string, any>, broadcast = true): void {
        this.client.updateEnvironment(data);
        if (broadcast) {
            this.broadcastEnvironment();
        }
    }

    initDispatchers(): void {
        // Forward analytics events sent from the SDK's AnalyticsClient
        this.client.addListener("event", (event: AnalyticsEvent) => {
            this.allEventsDispatcher.dispatch(event);
            this.eventsByTypeDispatcher.dispatch(event.type, event);
        });

        // Forward analytics events sent from the IFrames' AnalyticsClient
        iframeToSdkChannel.addListener(IFrameMessages.ANALYTICS_EVENT, ({event}: {event: AnalyticsEvent}) => {
            this.allEventsDispatcher.dispatch(event);
            this.eventsByTypeDispatcher.dispatch(event.type, event);
        });
    }
}

export const analyticsModule = new AnalyticsModule();
